import { useIntl } from "react-intl";

import {
  Row,
  Loader,
  Column,
  Container,
  ActionBar,
  ActionBarButtons,
  DetailsItem,
  EditActionButton,
  DeleteActionButton,
  AssignableUserList,
  BadRequestError,
  useNotifier,
  url,
  useAccessRightsQuery,
  useApi,
  useTranslateError,
  accessRightsShape,
} from "lcm-iot-commons";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

export default function TenantDetails() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const intl = useIntl();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const translateError = useTranslateError();

  const { data: accessRights, isLoading: isLoadingAccessRights } =
    useAccessRightsQuery("Tenant", {
      enabled: !!id,
    });
  const { data: tenant, isLoading: isLoadingTenants } = api.get.useQuery(
    `/tenants/${id}`,
    null,
    {
      enabled: !!id,
      onError: () => {},
    },
  );
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = api.getAll.useQuery(`/tenants/${id}/users`, null, {
    enabled: !!id,
    select: (response) => (response as $TSFixMe).users,
    onError: () => {},
  });
  const {
    data: adminsData,
    isLoading: isLoadingAdmins,
    refetch: refetchAdmins,
  } = api.getAll.useQuery(`/tenants/${id}/admins`, null, {
    enabled: !!id,
    select: (response) => (response as $TSFixMe).users,
    onError: () => {},
  });
  const { mutateAsync: createUser } = api.post.useMutation(
    `/tenants/${id}/users`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: createAdmin } = api.post.useMutation(
    `/tenants/${id}/admins`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: deleteUser } = api.delete.useMutation(
    `/tenants/${id}/users`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: deleteAdmin } = api.delete.useMutation(
    `/tenants/${id}/admins`,
    {
      onError: /* istanbul ignore next */ () => {},
    },
  );
  const { mutateAsync: deleteCurrentTenant } = api.delete.useMutation(
    `/tenants/${id}`,
    {
      onError: () => {},
    },
  );

  const isLoading = isLoadingTenants || isLoadingUsers || isLoadingAdmins;
  const users = usersData ?? [];
  const admins = adminsData ?? [];

  const assignUser = async (user: $TSFixMe) => {
    await createUser({ users: [{ id: user.id }] });
    refetchUsers();
  };

  const unassignUser = async (user: $TSFixMe) => {
    await deleteUser({ users: [{ id: user.id }] });
    refetchUsers();
  };

  const assignAdmin = async (admin: $TSFixMe) => {
    await createAdmin({ admins: [{ id: admin.id }] });
    refetchAdmins();
  };

  const unassignAdmin = async (admin: $TSFixMe) => {
    await deleteAdmin({ admins: [{ id: admin.id }] });
    refetchAdmins();
  };

  const deleteTenant = async () => {
    try {
      await deleteCurrentTenant();
      notifier.showSuccess(
        intl.formatMessage({ id: "tenants.actions.delete.notification" }),
      );
      navigate(url("/tenants"));
    } catch (error) {
      if (
        error instanceof BadRequestError &&
        error.contains("assigned_restriction")
      ) {
        notifier.showError(
          intl.formatMessage({ id: "api.error.tenant.assigned_restriction" }),
        );
      } else {
        notifier.showError(translateError(error));
      }
    }
  };

  return (
    <Container>
      <Row>
        <Column>
          <ActionBar>
            <h1 id="tenant-details-header">
              {intl.formatMessage({ id: "tenant_details.header" })}
            </h1>
            <ActionBarButtons>
              <EditActionButton
                id="edit-tenant-button"
                target={`/tenants/${id}/edit`}
                disabled={!(accessRights as accessRightsShape)?.canUpdate}
              />
              <DeleteActionButton
                id="delete-tenant-button"
                disabled={!(accessRights as accessRightsShape)?.canDelete}
                modalTitle={intl.formatMessage({
                  id: "tenants.actions.delete.modal_title",
                })}
                modalMessage={intl.formatMessage({
                  id: "tenants.actions.delete.modal_message",
                })}
                onConfirm={deleteTenant}
              />
            </ActionBarButtons>
          </ActionBar>
        </Column>
      </Row>
      {!isLoading && (
        <Row>
          {(tenant as $TSFixMe) && (
            <Column>
              <div>
                <DetailsItem
                  id="tenant-name"
                  translationKey="label.name"
                  value={(tenant as $TSFixMe).name}
                />
                <DetailsItem
                  id="tenant-description"
                  translationKey="label.description"
                  value={(tenant as $TSFixMe).description}
                />
              </div>
              {users && (
                <div id="user-list">
                  <h2>{intl.formatMessage({ id: "users.header" })}</h2>
                  <AssignableUserList
                    // @ts-expect-error TS(2322) FIXME: Type '{ users: never; onAssignUser: (admin: any) =... Remove this comment to see the full error message
                    users={users}
                    onAssignUser={assignUser}
                    onUnassignUser={unassignUser}
                    canUpdate={(accessRights as accessRightsShape)?.canUpdate}
                    acceptConnectUsers
                    showUserEmail
                  />
                </div>
              )}
              {admins && (
                <div id="admin-list">
                  <h2>
                    {intl.formatMessage({ id: "tenant_details.admins_header" })}
                  </h2>
                  <AssignableUserList
                    // @ts-expect-error TS(2322) FIXME: Type '{ users: never; onAssignUser: (admin: any) =... Remove this comment to see the full error message
                    users={admins}
                    onAssignUser={assignAdmin}
                    onUnassignUser={unassignAdmin}
                    canUpdate={(accessRights as accessRightsShape)?.canUpdate}
                    disableUnassignForLastUser
                    acceptConnectUsers
                    showUserEmail
                  />
                </div>
              )}
            </Column>
          )}
        </Row>
      )}
      <Loader
        id="tenant-details-loader"
        loading={isLoading || isLoadingAccessRights}
      />
    </Container>
  );
}
